import { useState } from 'react'
import { apiClient } from '@/shared/api/apiClient'
import type { ManualStandardRequirementItemWithValidation } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { parseWireEvent } from '@/features/compliance-studio/machinery-directive/steps/manual-validation/useValidateStandardStatements'

export function useValidateCraUserInformation(
  studioId: string,
  documentId?: string
) {
  const [isValidating, setIsValidating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [validationProgress, setValidationProgress] = useState<{
    current: number
    total: number
  } | null>(null)
  const [validationItems, setValidationItems] = useState<
    Map<string, ManualStandardRequirementItemWithValidation>
  >(new Map())

  const validate = async () => {
    if (documentId == null) {
      return
    }
    setIsValidating(true)
    setError(null)
    setProcessingId(null)
    setValidationProgress(null)
    setValidationItems(new Map())

    try {
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/cra-studio/${studioId}/user-information/validate`
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          'Accept-Language': navigator.language || 'en',
        },
        body: JSON.stringify({ documentId }),
      })

      if (!response.ok) {
        throw new Error(`Failed to start validation: ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body reader available')
      }

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) {
            continue
          }
          try {
            const raw = JSON.parse(line.slice(6)) as unknown
            const eventData = parseWireEvent(raw)
            if (eventData === null) {
              continue
            }

            if (eventData.type === 'validation_start') {
              setValidationProgress({
                current: 0,
                total: eventData.totalStatements,
              })
            } else if (eventData.type === 'statement_start') {
              if (eventData.id.length > 0) {
                setProcessingId(eventData.id)
              }
              if (eventData.totalStatements > 0) {
                setValidationProgress({
                  current: eventData.statementIndex - 1,
                  total: eventData.totalStatements,
                })
              }
            } else if (eventData.type === 'statement_result') {
              setValidationItems((prev) => {
                const next = new Map(prev)
                const entry: ManualStandardRequirementItemWithValidation = {
                  id: eventData.id,
                  text: eventData.text,
                  verified: eventData.verified,
                  explanation: eventData.explanation,
                }
                if (eventData.number.length > 0) {
                  entry.number = eventData.number
                }
                next.set(eventData.id, entry)
                return next
              })
              setProcessingId(null)
              setValidationProgress((prev) => {
                const total =
                  eventData.totalStatements > 0
                    ? eventData.totalStatements
                    : (prev?.total ?? 0)
                const completedByIndex =
                  eventData.statementIndex > 0
                    ? eventData.statementIndex
                    : null
                let nextCurrent = (prev?.current ?? 0) + 1
                if (completedByIndex != null) {
                  nextCurrent = completedByIndex
                }
                if (total <= 0) {
                  return prev
                }
                return { current: nextCurrent, total }
              })
            } else if (eventData.type === 'complete') {
              setIsValidating(false)
              setProcessingId(null)
              setValidationProgress(null)
            } else if (eventData.type === 'error') {
              setError(eventData.error)
              setIsValidating(false)
              setProcessingId(null)
              setValidationProgress(null)
            }
          } catch {
            // Ignore malformed SSE lines
          }
        }
      }

      setIsValidating(false)
      setProcessingId(null)
      setValidationProgress(null)
    } catch (err: unknown) {
      let message = 'Failed to validate user information'
      if (err instanceof Error) {
        message = err.message
      }
      setError(message)
      setIsValidating(false)
      setProcessingId(null)
      setValidationProgress(null)
    }
  }

  return {
    isValidating,
    error,
    processingId,
    validationProgress,
    validationItems,
    validate,
  }
}
